import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe2, ImagePlus, Layers3, LoaderCircle, LockKeyhole, SlidersHorizontal, X } from 'lucide-react';
import { fetchCategoriesApi } from '../../api/categories.api';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { classNames } from '../../utils/classNames';
import { buildEditForm, mergeCategories } from './projectFormUtils';

const FormSection = ({ title, description, icon: Icon, children, className }) => (
  <section
    className={classNames(
      'rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950 md:p-5',
      className
    )}
  >
    <div className="mb-4 flex items-start gap-3">
      {Icon ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
          <Icon size={16} />
        </span>
      ) : null}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-neutral-950 dark:text-white">{title}</h3>
        {description ? <p className="mt-1 text-xs leading-5 text-neutral-500">{description}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

export const ProjectEditForm = ({ project, error, saving, onSubmit }) => {
  const { t } = useI18n();
  const coverInputRef = useRef(null);
  const [form, setForm] = useState(() => buildEditForm(project));
  const [nameError, setNameError] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');

  useEffect(() => {
    setForm(buildEditForm(project));
    setNameError('');
  }, [project?.id, project?.updatedAt]);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError('');

      try {
        const items = await fetchCategoriesApi();

        if (!cancelled) {
          setCategories(items);
        }
      } catch (requestError) {
        if (!cancelled) {
          setCategories([]);
          setCategoriesError(requestError.message || t('projectCategoriesLoadError'));
        }
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!form.coverImageFile) {
      setCoverPreviewUrl(form.coverImageUrl || '');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(form.coverImageFile);
    setCoverPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form.coverImageFile, form.coverImageUrl]);

  const categoryOptions = useMemo(
    () => mergeCategories(categories, project?.categories || []),
    [categories, project?.categories]
  );

  const isPublic = form.visibility === 'public';

  const toggleCategory = (categoryId) => {
    setForm((current) => ({
      ...current,
      categoryIds: current.categoryIds.includes(categoryId)
        ? current.categoryIds.filter((id) => id !== categoryId)
        : [...current.categoryIds, categoryId]
    }));
  };

  const handleCoverSelect = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setForm((current) => ({
      ...current,
      coverImageFile: file
    }));
  };

  const openCoverPicker = () => {
    if (saving) {
      return;
    }

    coverInputRef.current?.click();
  };

  const clearCoverSelection = () => {
    setForm((current) => ({
      ...current,
      coverImageFile: null,
      coverImageUrl: project?.coverImageUrl || project?.coverImage || ''
    }));

    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    const trimmedName = form.name.trim();

    if (!trimmedName) {
      setNameError(t('projectNameRequired'));
      return;
    }

    if (saving) {
      return;
    }

    setNameError('');

    await onSubmit({
      id: project.id,
      name: trimmedName,
      description: form.description.trim(),
      visibility: form.visibility,
      sortOrder: Number(form.sortOrder) || 0,
      categoryIds: form.categoryIds,
      coverImageFile: form.coverImageFile
    });
  };

  const visibilityOptions = [
    {
      value: 'your',
      label: t('private'),
      hint: t('projectVisibilityPrivateHint'),
      icon: LockKeyhole
    },
    {
      value: 'public',
      label: t('public'),
      hint: t('projectVisibilityPublicHint'),
      icon: Globe2
    }
  ];

  return (
    <form id="project-edit-form" className="space-y-6" onSubmit={submitHandler}>
      <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:items-start">
        <aside className="space-y-4 lg:sticky lg:top-4">
          <FormSection title={t('projectCoverImage')} description={t('projectCoverImageHint')} icon={ImagePlus}>
            <input
              ref={coverInputRef}
              accept="image/*"
              className="sr-only"
              disabled={saving}
              onChange={handleCoverSelect}
              type="file"
            />

            <button
              type="button"
              disabled={saving}
              onClick={openCoverPicker}
              className="group relative w-full overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 text-start transition hover:border-neutral-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700"
              aria-label={t('projectCoverImageChoose')}
            >
              {coverPreviewUrl ? (
                <img alt="" className="aspect-[8/5] w-full object-cover sm:aspect-[3/2]" src={coverPreviewUrl} />
              ) : (
                <div className="flex aspect-[8/5] flex-col items-center justify-center gap-2 text-neutral-400 sm:aspect-[3/2]">
                  <ImagePlus size={28} />
                  <span className="px-4 text-center text-xs">{t('projectCoverImageEmpty')}</span>
                </div>
              )}

              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-neutral-950/0 transition group-hover:bg-neutral-950/40 group-focus-visible:bg-neutral-950/40">
                <span className="inline-flex items-center gap-2 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-neutral-900 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100 dark:bg-neutral-950/90 dark:text-white">
                  <ImagePlus size={14} />
                  {t('projectCoverImageClickHint')}
                </span>
              </span>
            </button>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" variant="secondary" className="flex-1" disabled={saving} onClick={openCoverPicker}>
                <ImagePlus size={16} />
                {t('projectCoverImageChoose')}
              </Button>
              {form.coverImageFile || coverPreviewUrl ? (
                <Button type="button" variant="ghost" disabled={saving} onClick={clearCoverSelection}>
                  <X size={16} />
                  {t('projectCoverImageRemove')}
                </Button>
              ) : null}
            </div>
          </FormSection>

          <FormSection
            title={t('projectVisibility')}
            description={t('projectEditVisibilityHint')}
            icon={isPublic ? Globe2 : LockKeyhole}
          >
            <div className="space-y-2">
              {visibilityOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = form.visibility === option.value;

                return (
                  <label
                    key={option.value}
                    className={classNames(
                      'flex cursor-pointer gap-3 rounded-xl border p-3 transition',
                      isSelected
                        ? 'border-neutral-300 bg-[#F5F5F5] dark:border-neutral-700 dark:bg-neutral-900'
                        : 'border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-900'
                    )}
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="project-visibility"
                      value={option.value}
                      checked={isSelected}
                      disabled={saving}
                      onChange={() => setForm({ ...form, visibility: option.value })}
                    />
                    <span
                      className={classNames(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                        isSelected
                          ? 'border-neutral-200 bg-white text-neutral-900 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white'
                          : 'border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400'
                      )}
                    >
                      <Icon size={15} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-neutral-950 dark:text-white">{option.label}</span>
                      <span className="mt-0.5 block text-[11px] leading-5 text-neutral-500">{option.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </FormSection>

        </aside>

        <div className="space-y-4">
          <FormSection
            title={t('projectEditDetailsTitle')}
            description={t('projectModalEditSubtitle')}
            icon={SlidersHorizontal}
          >
            <div className="space-y-4">
              <Input
                label={t('projectName')}
                value={form.name}
                disabled={saving}
                required
                maxLength={80}
                placeholder={t('projectNamePlaceholder')}
                onChange={(event) => {
                  setForm({ ...form, name: event.target.value });
                  if (nameError) {
                    setNameError('');
                  }
                }}
              />
              {nameError ? (
                <p className="-mt-2 text-sm text-rose-600 dark:text-rose-300" role="alert">
                  {nameError}
                </p>
              ) : null}

              <Textarea
                label={t('projectDescription')}
                value={form.description}
                disabled={saving}
                maxLength={280}
                placeholder={t('newProjectHint')}
                className="min-h-[120px]"
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />

              <Input
                label={t('projectSortOrder')}
                type="number"
                min={0}
                step={1}
                value={form.sortOrder}
                disabled={saving}
                onChange={(event) => setForm({ ...form, sortOrder: event.target.value })}
              />
            </div>
          </FormSection>

          <FormSection
            title={t('projectCategories')}
            description={t('projectCategoriesHint')}
            icon={Layers3}
          >
            {categoriesLoading ? (
              <div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-3 text-sm text-neutral-500 dark:border-neutral-800">
                <LoaderCircle size={16} className="animate-spin" />
                {t('projectCategoriesLoading')}
              </div>
            ) : null}

            {categoriesError ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-xs text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
                {categoriesError}
              </p>
            ) : null}

            {!categoriesLoading && categoryOptions.length === 0 ? (
              <p className="rounded-xl border border-dashed border-neutral-200 px-4 py-3 text-xs text-neutral-500 dark:border-neutral-800">
                {t('projectCategoriesEmpty')}
              </p>
            ) : null}

            {categoryOptions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((category) => {
                  const isSelected = form.categoryIds.includes(category.id);

                  return (
                    <button
                      key={category.id}
                      type="button"
                      disabled={saving}
                      onClick={() => toggleCategory(category.id)}
                      className={classNames(
                        'rounded-lg border px-3 py-1.5 text-xs font-medium transition',
                        isSelected
                          ? 'border-neutral-900 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950'
                          : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300 dark:hover:bg-neutral-900'
                      )}
                    >
                      {category.name}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </FormSection>
        </div>
      </div>

      {error ? (
        <p
          className="rounded-[1.15rem] border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </form>
  );
};
